import { useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { CardForm, type CardFormHandle } from '../components/LazyCardForm';
import { Notice } from '../components/Notice';
import { errorMessage, post } from '../lib/api';
import { useSession } from '../state/session';
import { PageShell } from './ProfilePage';

/** Lets a signed-in user add or replace the card used by the subscription. */
export function AddCardPage() {
  const { user, config, refresh } = useSession();
  const [, navigate] = useLocation();
  const cardRef = useRef<CardFormHandle>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const billing = config?.billing;
  // Set after a new Google sign-up: the card completes the sign-up, as in the e-mail form.
  const [isNewAccount] = useState(() => new URLSearchParams(window.location.search).get('novo') === '1');
  const trialDays = config?.trialDays ?? 45;

  async function save() {
    if (!user || !cardRef.current) return;
    setBusy(true);
    setError(null);
    try {
      const paymentMethodId = await cardRef.current.createPaymentMethod({ name: user.name, email: user.email });
      await post('/api/billing/payment-method', { paymentMethodId });
      await refresh();
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageShell title={isNewAccount ? 'Conclua seu cadastro' : 'Cartão de crédito'} back={isNewAccount ? '/' : '/perfil'}>
      <section className="card narrow">
        {user?.isTest ? (
          <Notice kind="info">Esta é uma conta de teste: não há cobrança nem cartão. Para passar ao plano normal, fale com o suporte.</Notice>
        ) : !billing?.enabled || !billing.publishableKey ? (
          <Notice kind="info">Pagamentos ainda não estão disponíveis. Fale com o administrador.</Notice>
        ) : done ? (
          <>
            <Notice kind="success">Cartão salvo com sucesso.</Notice>
            <button type="button" className="btn btn--primary" onClick={() => navigate('/')}>
              Voltar ao quadro
            </button>
          </>
        ) : (
          <>
            {isNewAccount && (
              <p>
                <b>Conta criada!</b> Para começar os {trialDays} dias grátis, informe o cartão de crédito. Nada é cobrado durante o
                teste, e você pode cancelar quando quiser.
              </p>
            )}
            <p>
              O cartão é enviado diretamente ao Stripe, nosso processador de pagamentos. O Voz Livre não armazena o número do
              cartão.
            </p>
            <CardForm ref={cardRef} publishableKey={billing.publishableKey} />
            {error && <Notice kind="error">{error}</Notice>}
            <button type="button" className="btn btn--primary btn--block" onClick={() => void save()} disabled={busy}>
              {busy ? 'Salvando…' : 'Salvar cartão'}
            </button>
            {isNewAccount && (
              <button type="button" className="btn btn--ghost btn--block" onClick={() => navigate('/')} disabled={busy}>
                Agora não
              </button>
            )}
          </>
        )}
      </section>
    </PageShell>
  );
}
